import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../../store";
import ticketingConfigApi from "../../../api/ticketingConfigApi";
import {
  TeamAssignmentUpdateRequest,
  TeamResponse,
  TertiaryCategoryResponse,
  TicketingCategoryDTO,
  TicketingSubCategoryDTO,
  ReviewFieldConfig,
} from "../../../types/ticketingConfig.types";
import { TeamAssignmentRow, TeamEntry } from "../TeamAssignment";
import { CategoryItem, SubcategoryItem, TertiaryItem } from "../CategoryMapping";
import useSustainTeamMembers from "./useSustainTeamMembers";
import { TEAM_COLORS } from "../components/SustainTeamTypes";

export const getApiErrorMessage = (err: any): string =>
  err?.response?.data?.message || err?.message || "Something went wrong. Please try again.";

/**
 * Fetches Team Assignment + Category Mapping data from /ticketing/config/* and
 * exposes mapped view models plus CRUD handlers for the Ticketing Configurations screen.
 */
export const useTicketingConfig = () => {
  const [teams, setTeams] = useState<TeamResponse[]>([]);
  const [teamsLoading, setTeamsLoading] = useState(true);

  const [rawAssignments, setRawAssignments] = useState<
    Awaited<ReturnType<typeof ticketingConfigApi.getTeamAssignments>>["content"]
  >([]);
  const [assignmentsLoading, setAssignmentsLoading] = useState(true);

  const [categories, setCategories] = useState<TicketingCategoryDTO[]>([]);
  const [subcategoriesByCategory, setSubcategoriesByCategory] = useState<
    Record<number, TicketingSubCategoryDTO[]>
  >({});
  const [subcategoriesLoading, setSubcategoriesLoading] = useState(false);
  const [tertiariesBySubcategory, setTertiariesBySubcategory] = useState<
    Record<number, TertiaryCategoryResponse[]>
  >({});
  const [tertiariesLoading, setTertiariesLoading] = useState(false);
  const [categoriesLoading, setCategoriesLoading] = useState(true);

  const [error, setError] = useState<string | null>(null);

  // Sustain Team Lead vs Member — the backend has no per-member "team" key yet, so
  // Leads get full write access and Members are read-only here until that exists.
  const currentUserEmail = useSelector(
    (state: RootState) => state.appData.userInfo?.email ?? ""
  ).toLowerCase();
  const { members: sustainRoster, loading: sustainRosterLoading } = useSustainTeamMembers("");
  const isSustainTeamLead = useMemo(
    () => sustainRoster.some((m) => m.email.toLowerCase() === currentUserEmail && m.role === "lead"),
    [sustainRoster, currentUserEmail]
  );

  // The teamId of the team the current user belongs to (null only when the roster
  // entry has no team assigned). Note: Leads also carry a teamId — the caller is
  // responsible for only acting on this value when the user is a Member (not Lead).
  const currentUserTeamId: number | null = useMemo(
    () =>
      sustainRoster.find((m) => m.email.toLowerCase() === currentUserEmail)?.teamId ?? null,
    [sustainRoster, currentUserEmail]
  );

  const fetchTeams = useCallback(async () => {
    setTeamsLoading(true);
    try {
      const data = await ticketingConfigApi.getTeams();
      setTeams(data);
    } catch (e) {
      setError(getApiErrorMessage(e));
    } finally {
      setTeamsLoading(false);
    }
  }, []);

  const fetchAssignments = useCallback(async () => {
    setAssignmentsLoading(true);
    try {
      // Fetch a large single page — Team Assignment table paginates/searches client-side.
      const page = await ticketingConfigApi.getTeamAssignments({ page: 0, size: 1000 });
      setRawAssignments(page.content);
    } catch (e) {
      setError(getApiErrorMessage(e));
    } finally {
      setAssignmentsLoading(false);
    }
  }, []);

  const fetchCategories = useCallback(async () => {
    setCategoriesLoading(true);
    try {
      const cats = (await ticketingConfigApi.getCategories()).map((c) => ({
        ...c,
        subCategories: c.subCategories ?? [],
      }));
      setCategories(cats);
      // Nested subcategories are the fresh source of truth again — drop any stale per-category overrides.
      setSubcategoriesByCategory({});

      const allSubIds = cats.flatMap((c) => c.subCategories.map((s) => s.id));
      const tertiaryResults = await Promise.all(
        allSubIds.map((id) =>
          ticketingConfigApi.getTertiaryCategories(id).catch(() => [] as TertiaryCategoryResponse[])
        )
      );
      const map: Record<number, TertiaryCategoryResponse[]> = {};
      allSubIds.forEach((id, idx) => {
        map[id] = tertiaryResults[idx];
      });
      setTertiariesBySubcategory(map);
    } catch (e) {
      setError(getApiErrorMessage(e));
    } finally {
      setCategoriesLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTeams();
    fetchAssignments();
    fetchCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Mapped view models ──────────────────────────────────────────────────

  const teamEntries: TeamEntry[] = useMemo(
    () =>
      teams.map((t, idx) => ({
        key: String(t.id),
        name: t.name,
        short: t.name,
        color: TEAM_COLORS[idx % TEAM_COLORS.length],
        active: t.isActive ?? t.active ?? true,
      })),
    [teams]
  );

  const categoryItems: CategoryItem[] = useMemo(
    () =>
      categories.map((c) => ({
        id: String(c.id),
        name: c.name,
        active: c.active ?? c.isActive ?? true,
        subcategoryCount: c.subcategoryCount ?? c.subCategories?.length ?? 0,
      })),
    [categories]
  );

  const subcategoryItems: SubcategoryItem[] = useMemo(
    () =>
      categories.flatMap((c) => {
        const subs = subcategoriesByCategory[c.id] ?? c.subCategories ?? [];
        return subs.map((s) => ({
          id: String(s.id),
          categoryId: String(c.id),
          name: s.name,
          active: s.active ?? s.isActive ?? true,
          tertiaryCategoryCount: s.tertiaryCategoryCount ?? 0,
        }));
      }),
    [categories, subcategoriesByCategory]
  );

  const loadSubcategoriesForCategory = useCallback(async (categoryId: string) => {
    const numId = Number(categoryId);
    if (!Number.isFinite(numId)) return;
    setSubcategoriesLoading(true);
    try {
      const subs = await ticketingConfigApi.getSubCategories(numId);
      setSubcategoriesByCategory((prev) => ({ ...prev, [numId]: subs }));
    } catch (e) {
      setError(getApiErrorMessage(e));
    } finally {
      setSubcategoriesLoading(false);
    }
  }, []);

  const loadTertiariesForSubcategory = useCallback(async (subcategoryId: string) => {
    const numId = Number(subcategoryId);
    if (!Number.isFinite(numId)) return;
    setTertiariesLoading(true);
    try {
      const tertiaryValues = await ticketingConfigApi.getTertiaryCategories(numId);
      setTertiariesBySubcategory((prev) => ({ ...prev, [numId]: tertiaryValues }));
    } catch (e) {
      setError(getApiErrorMessage(e));
    } finally {
      setTertiariesLoading(false);
    }
  }, []);

  const tertiaryItems: TertiaryItem[] = useMemo(() => {
    const list: TertiaryItem[] = [];
    Object.entries(tertiariesBySubcategory).forEach(([subId, values]) => {
      values.forEach((t) =>
        list.push({ id: String(t.id), subcategoryId: subId, name: t.name, active: t.isActive })
      );
    });
    return list;
  }, [tertiariesBySubcategory]);

  // Still needed by toggleSubcategory (resolves a subcategory's parent category id).
  const subcategoryById = useMemo(() => {
    const map = new Map<number, { name: string; categoryId: number }>();
    categories.forEach((c) => {
      const subs = subcategoriesByCategory[c.id] ?? c.subCategories ?? [];
      subs.forEach((s) => map.set(s.id, { name: s.name, categoryId: c.id }));
    });
    return map;
  }, [categories, subcategoriesByCategory]);

  // Backend now returns the full category/subCategory/tertiary ancestry per row directly,
  // no more nodeType/nodeId lookup resolution needed.
  const assignmentRows: TeamAssignmentRow[] = useMemo(
    () =>
      rawAssignments.map((r) => ({
        id: String(r.id),
        category: r.categoryName ?? "",
        subcategory: r.subCategoryName ?? "",
        tertiary: r.tertiaryName ?? "",
        org: r.organizationName ?? r.organizationId ?? "",
        ag: r.snowAssignmentGroup ?? "",
        team: r.team?.name ?? "",
        status: r.active ? "active" : "inactive",
        teamAssignmentInactive: r.teamAssignmentInactive,
      })),
    [rawAssignments]
  );

  // ── Mutations ────────────────────────────────────────────────────────────

  const saveTeams = useCallback(
    async (updatedEntries: TeamEntry[]) => {
      const originalById = new Map(teams.map((t) => [String(t.id), t]));
      try {
        for (const entry of updatedEntries) {
          const original = originalById.get(entry.key);
          if (!original) {
            await ticketingConfigApi.createTeam({ name: entry.name });
          } else if ((original.isActive ?? original.active ?? true) !== entry.active) {
            await ticketingConfigApi.setTeamStatus(original.id, entry.active);
          }
        }
      } finally {
        await fetchTeams();
        await fetchAssignments();
      }
    },
    [teams, fetchAssignments, fetchTeams]
  );

  const updateAssignment = useCallback(
    async (id: string, body: TeamAssignmentUpdateRequest) => {
      try {
        await ticketingConfigApi.updateTeamAssignment(Number(id), body);
      } finally {
        await fetchAssignments();
      }
    },
    [fetchAssignments]
  );

  const addCategory = useCallback(
    async (name: string) => {
      try {
        await ticketingConfigApi.createCategory({ name });
      } finally {
        await fetchCategories();
      }
    },
    [fetchCategories]
  );

  const addSubcategory = useCallback(
    async (categoryId: string, name: string, fields?: ReviewFieldConfig[]) => {
      try {
        await ticketingConfigApi.createSubCategories(Number(categoryId), fields?.length
          ? { subCategories: [{ name, fields }] }
          : { names: [name] });
      } finally {
        await fetchCategories();
        await loadSubcategoriesForCategory(categoryId);
      }
    },
    [fetchCategories, loadSubcategoriesForCategory]
  );

  const addTertiary = useCallback(
    async (categoryId: string, subcategoryId: string, name: string, fields?: ReviewFieldConfig[]) => {
      try {
        await ticketingConfigApi.createTertiaryCategories(Number(subcategoryId), fields?.length
          ? { parentCategoryId: Number(categoryId), tertiaries: [{ name, fields }] }
          : { names: [name] });
      } finally {
        await fetchCategories();
          await loadSubcategoriesForCategory(categoryId);
          await loadTertiariesForSubcategory(subcategoryId);
        }
    },
    [fetchCategories, loadSubcategoriesForCategory, loadTertiariesForSubcategory]
  );

  const editCategory = useCallback(
    async (id: string, name: string) => {
      try {
        await ticketingConfigApi.updateCategory(Number(id), { name });
      } finally {
        await fetchCategories();
      }
    },
    [fetchCategories]
  );

  const editSubcategory = useCallback(
    async (id: string, name: string) => {
      try {
        await ticketingConfigApi.updateSubCategory(Number(id), { name });
      } finally {
        await fetchCategories();
      }
    },
    [fetchCategories]
  );

  const editTertiary = useCallback(
    async (id: string, name: string) => {
      try {
        await ticketingConfigApi.updateTertiary(Number(id), { name });
      } finally {
        await fetchCategories();
      }
    },
    [fetchCategories]
  );

  const toggleCategory = useCallback(
    async (id: string, active: boolean) => {
      try {
        await ticketingConfigApi.updateCategory(Number(id), { active });
      } finally {
        await fetchCategories();
      }
    },
    [fetchCategories]
  );

  const toggleSubcategory = useCallback(
    async (id: string, active: boolean) => {
      try {
        await ticketingConfigApi.updateSubCategory(Number(id), { active });
      } finally {
        const parentCategoryId = subcategoryById.get(Number(id))?.categoryId;
        if (Number.isFinite(parentCategoryId)) {
          await loadSubcategoriesForCategory(String(parentCategoryId));
        } else {
          await fetchCategories();
        }
      }
    },
    [fetchCategories, loadSubcategoriesForCategory, subcategoryById]
  );

  const toggleTertiary = useCallback(
    async (id: string, active: boolean) => {
      try {
        await ticketingConfigApi.updateTertiary(Number(id), { active });
      } finally {
        await fetchCategories();
      }
    },
    [fetchCategories]
  );

  return {
    teams: teamEntries,
    teamsLoading,
    assignmentRows,
    assignmentsLoading,
    categories: categoryItems,
    subcategories: subcategoryItems,
    tertiaries: tertiaryItems,
    categoriesLoading,
    subcategoriesLoading,
    loadSubcategoriesForCategory,
    tertiariesLoading,
    loadTertiariesForSubcategory,
    isSustainTeamLead,
    currentUserTeamId,
    sustainRoleLoading: sustainRosterLoading,
    error,
    refetchCategories: fetchCategories,
    saveTeams,
    refetchTeams: fetchTeams,
    updateAssignment,
    refetchAssignments: fetchAssignments,
    addCategory,
    addSubcategory,
    addTertiary,
    editCategory,
    editSubcategory,
    editTertiary,
    toggleCategory,
    toggleSubcategory,
    toggleTertiary,
  };
};

export default useTicketingConfig;
